import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import {
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Typography,
  FormHelperText,
} from '@material-ui/core';
import moment from 'moment-timezone';
import DateFnsUtils from '@date-io/date-fns';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { uniqueId } from 'lodash';
import frLocale from 'date-fns/locale/fr';
import { CheckCircle } from '@material-ui/icons';
import messages from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/messages';
import { translate } from 'containers/services/translate';
import { Listings, Config } from 'containers/AppContainer/typings';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import {
  LABEL_WIDTH,
  MAX_LENGTH,
  SUED_PARTY_DRIVER_ID,
  CHOICE,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/constants';
import {
  Idetails,
  ISuedParty,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/typings';
import { DETAIL_JUGEMENT_ADD } from 'containers/data-structure';
import { PROCEDURE_NATURES } from 'containers/LawyerLegalProceedingsPage/constants';
import {
  addDaysToDate,
  getIsInsuredSuedValue,
  validationCourtFileNumber,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import {
  IsSuedPartyChecked,
  IsVehiculeChecked,
  isDriverSelected,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import { SuedPartiesContainer } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/styled-components';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import { DATE_FORMAT } from 'constants/global';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import { getVehicleFromSagisInfo } from 'containers/PreOpeningPage/OpposingParty/utils';

export interface ProcedureInformationFormProps {
  intl: IntlShape;
  isDrawer?: boolean;
  listings: Listings;
  details: Idetails;
  setDetails: Function;
  shouldDisplay?: boolean;
  vehicules?: Vehicule[];
  processInformationValidation?: any;
  messageError?: any;
  lossDate: string;
  sagisOpposingParties: any;
  currentJudiciaryProcess: any;
}

const ProcedureInformationForm: React.FC<ProcedureInformationFormProps> = ({
  listings,
  details,
  setDetails,
  isDrawer,
  vehicules,
  processInformationValidation,
  messageError,
  ...props
}): JSX.Element => {
  const [isInputChanged, setIsInputChanged] = useState<boolean>(true);
  const [errorDate, setErrorDate] = useState<boolean>(false);
  const [errorCourtFile, setErrorCourtFile] = useState<boolean>(false);

  useEffect(() => {
    const procedureDetails = { ...details };

    procedureDetails[DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID] = String(
      get(props.currentJudiciaryProcess, 'procedureNatureId', null),
    );
    setDetails(procedureDetails);
  }, [props.currentJudiciaryProcess]);
  /* istanbul ignore next */
  const handleProcedureChange = (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    setIsInputChanged(false);
    const procedureDetails = { ...details };

    procedureDetails[DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID] = String(
      event.target.value,
    );
    procedureDetails[DETAIL_JUGEMENT_ADD.IS_INSURED_SUED] = null;
    procedureDetails[DETAIL_JUGEMENT_ADD.PACKAGE_CONTINUATION] = null;

    setDetails(procedureDetails);
  };

  const handleFormChange = (fieldName: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    if (fieldName === DETAIL_JUGEMENT_ADD.COURT_FILE) {
      setErrorCourtFile(false);
      if (!validationCourtFileNumber(event.target.value)) {
        setErrorCourtFile(true);
      }
    }
    setIsInputChanged(false);
    setDetails({
      ...details,
      [fieldName]: event.target.value,
    });
  };

  const handleChangeCheckBox = (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    const procedureDetails = { ...details };
    procedureDetails[DETAIL_JUGEMENT_ADD.IS_INSURED_SUED] = !!(
      event.target.value === CHOICE.YES
    );
    setDetails(procedureDetails);
  };

  const handleSuedPartyChangeCheckBox = (suedPartyId: number) => (
    event: React.ChangeEvent<{ value: string; checked: boolean }>,
  ) => {
    const procedureDetails = { ...details };
    const selectedSuedParty = listings.suedParty.find(
      (el: Config) => el.id === suedPartyId,
    );

    if (event.target.checked) {
      procedureDetails[DETAIL_JUGEMENT_ADD.SUED_PARTIES]?.push({
        suedParty: selectedSuedParty?.id,
        title: selectedSuedParty?.title,
        vehicules: [],
      });
    } else {
      procedureDetails[DETAIL_JUGEMENT_ADD.SUED_PARTIES].splice(
        procedureDetails[DETAIL_JUGEMENT_ADD.SUED_PARTIES].findIndex(
          (el: ISuedParty) => el.suedParty === selectedSuedParty?.id,
        ),
        1,
      );
    }

    setDetails(procedureDetails);
  };

  const handleVehiculeChangeCheckBox = (vehicule: Vehicule) => (
    event: React.ChangeEvent<{ value: string; checked: boolean }>,
  ) => {
    const procedureDetails = { ...details };
    const driverSuedParty = procedureDetails[
      DETAIL_JUGEMENT_ADD.SUED_PARTIES
    ].find((el: ISuedParty) => el.suedParty === SUED_PARTY_DRIVER_ID);

    if (event.target.checked) {
      driverSuedParty?.vehicules.push(vehicule);
    } else {
      driverSuedParty?.vehicules.splice(
        driverSuedParty?.vehicules.indexOf(vehicule),
        1,
      );
    }

    setDetails(procedureDetails);
  };

  const setVehicules = vehicules;
  /* istanbul ignore next */
  const handleChangeDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    setIsInputChanged(false);
    setErrorDate(false);
    const hearingDate = moment(theDate as Date).format(DATE_FORMAT);
    setDetails({ ...details, hearingDate: hearingDate });

    if (addDaysToDate(props.lossDate || '', 1)) {
      if (
        //@ts-ignore
        new Date(addDaysToDate(props.lossDate || '', 1)) > new Date(hearingDate)
      ) {
        setErrorDate(true);
      }
    }
  };
  return (
    <section style={{ padding: 10 }}>
      {props.shouldDisplay && (
        <Typography className="procedure-detail-bloc-title">
          {translate(messages.procedureInformationTitle, props)}
        </Typography>
      )}
      <article className="form-wrapper">
        <FormControl
          className="formEl"
          error={
            !!get(processInformationValidation, [
              DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID,
            ]) && !isInputChanged
          }
        >
          <InputLabel id="procedure_nature-label-id">
            {translate(messages.procedureNature, props)}
          </InputLabel>
          <Select
            labelId="procedure_nature-label-id"
            onChange={handleProcedureChange}
            name={DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID}
            value={get(details, [DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID], '')}
            labelWidth={LABEL_WIDTH}
            disabled={true}
          >
            {!isEmpty(listings.procedureNature) &&
              listings.procedureNature.map((el: Config) => (
                <MenuItem key={el.id} value={String(el.id)}>
                  {el.title}
                </MenuItem>
              ))}
          </Select>
          {!!get(processInformationValidation, [
            DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID,
          ]) &&
            !isInputChanged &&
            messageError &&
            messageError && (
              <FormHelperText className="errorSelect">
                {messageError[DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID]}
              </FormHelperText>
            )}
        </FormControl>
        <FormControl
          className="formEl"
          error={
            !!get(processInformationValidation, [
              DETAIL_JUGEMENT_ADD.JURISDICTION_ID,
            ]) && !isInputChanged
          }
        >
          <InputLabel id="jurisdiction-label-id">
            {translate(messages.jurisdiction, props)}
          </InputLabel>
          <Select
            labelId="jurisdiction-label-id"
            onChange={handleFormChange(DETAIL_JUGEMENT_ADD.JURISDICTION_ID)}
            name={DETAIL_JUGEMENT_ADD.JURISDICTION_ID}
            value={get(details, [DETAIL_JUGEMENT_ADD.JURISDICTION_ID], '')}
            labelWidth={LABEL_WIDTH}
          >
            {!isEmpty(listings.jurisdiction) &&
              listings.jurisdiction.map((el: Config) => (
                <MenuItem key={el.id} value={el.id}>
                  {el.title}
                </MenuItem>
              ))}
          </Select>
          {!!get(processInformationValidation, [
            DETAIL_JUGEMENT_ADD.JURISDICTION_ID,
          ]) &&
            !isInputChanged &&
            messageError && (
              <FormHelperText className="errorSelect">
                {messageError[DETAIL_JUGEMENT_ADD.JURISDICTION_ID]}
              </FormHelperText>
            )}
        </FormControl>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
          <KeyboardDatePicker
            autoOk
            minDate={addDaysToDate(props.lossDate || '', 1)}
            variant="inline"
            invalidDateMessage={''}
            format="dd/MM/yyyy"
            id="date-picker-inline-victim-date-of-birth"
            label={translate({ ...messages.hearingDateTitle }, props)}
            name={DETAIL_JUGEMENT_ADD.HEARING_DATE}
            value={dateStringToLocalDate(
              get(details, [DETAIL_JUGEMENT_ADD.HEARING_DATE]),
            )}
            onChange={handleChangeDate}
            error={
              (!!get(processInformationValidation, [
                DETAIL_JUGEMENT_ADD.HEARING_DATE,
              ]) &&
                !isInputChanged) ||
              errorDate ||
              get(details, [DETAIL_JUGEMENT_ADD.HEARING_DATE]) ===
                'Invalid date'
            }
            helperText={
              (!isInputChanged &&
                messageError &&
                messageError[DETAIL_JUGEMENT_ADD.HEARING_DATE]) ||
              (errorDate && translate(messages.hearingDateError, props)) ||
              (get(details, [DETAIL_JUGEMENT_ADD.HEARING_DATE]) ===
                'Invalid date' &&
                translate(messages.invalidDate, props))
            }
            style={{
              top: props.shouldDisplay ? '2.5px' : 'unset',
              width: props.shouldDisplay ? '21%' : 'unset',
              marginTop: !props.shouldDisplay ? '16px' : 'unset',
            }}
          />
        </MuiPickersUtilsProvider>
        <TextField
          id="standard-basic"
          label={translate(messages.courtFile, props)}
          className="formEl"
          placeholder={'NNNNNNN/NNNN/NNNN'}
          onChange={handleFormChange(DETAIL_JUGEMENT_ADD.COURT_FILE)}
          name={DETAIL_JUGEMENT_ADD.COURT_FILE}
          data-testId={DETAIL_JUGEMENT_ADD.COURT_FILE}
          value={get(details, [DETAIL_JUGEMENT_ADD.COURT_FILE], '')}
          helperText={
            (!isInputChanged &&
              messageError &&
              messageError[DETAIL_JUGEMENT_ADD.COURT_FILE]) ||
            (!isInputChanged &&
              !validationCourtFileNumber(
                get(details, [DETAIL_JUGEMENT_ADD.COURT_FILE], ''),
              ) &&
              translate(messages.courtFileNumberError, props))
          }
          error={
            (!!get(processInformationValidation, [
              DETAIL_JUGEMENT_ADD.COURT_FILE,
            ]) &&
              !isInputChanged) ||
            (!isInputChanged &&
              errorCourtFile &&
              !validationCourtFileNumber(
                get(details, [DETAIL_JUGEMENT_ADD.COURT_FILE], ''),
              ))
          }
          style={{ top: '2.5px' }}
        />
      </article>
      {String(details[DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID]) ===
        PROCEDURE_NATURES.CORRECTIONAL && (
        <article className="other-form-wrapper">
          <TextField
            id="poursuite_parquet"
            label={translate(messages.packageContinuation, props)}
            onChange={handleFormChange(
              DETAIL_JUGEMENT_ADD.PACKAGE_CONTINUATION,
            )}
            name={DETAIL_JUGEMENT_ADD.PACKAGE_CONTINUATION}
            value={get(details, [DETAIL_JUGEMENT_ADD.PACKAGE_CONTINUATION], '')}
            inputProps={{
              maxLength: MAX_LENGTH,
            }}
            className="textBox"
          />
          <article className="is-insured-sued">
            {!featureService.hasFeature(FEATURES.SUED_PARTY) ? (
              <>
                <span className="checkbox-form-label">
                  {translate(messages.isInsuredSued, props)}
                </span>
                <RadioGroup
                  row
                  onChange={handleChangeCheckBox}
                  value={getIsInsuredSuedValue(
                    details[DETAIL_JUGEMENT_ADD.IS_INSURED_SUED],
                  )}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.YES}
                        checkedIcon={<CheckCircle />}
                      />
                    }
                    label={translate(messages.yes, props)}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.NO}
                        checkedIcon={<CheckCircle />}
                      />
                    }
                    label={translate(messages.no, props)}
                  />
                </RadioGroup>
              </>
            ) : (
              <>
                <FormGroup>
                  <div className="sued-party-title">
                    <span className="checkbox-form-label">
                      {translate(messages.suedParties, props)}
                    </span>
                  </div>
                  {listings.suedParty?.map((suedParty, index) => (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          data-testid="checkboxId"
                          checked={IsSuedPartyChecked(
                            suedParty.id,
                            details[DETAIL_JUGEMENT_ADD.SUED_PARTIES],
                          )}
                          color="primary"
                          onChange={handleSuedPartyChangeCheckBox(suedParty.id)}
                        />
                      }
                      label={suedParty.title}
                    />
                  ))}
                </FormGroup>
                <SuedPartiesContainer>
                  <div className="sued_party_container">
                    {isDriverSelected(details) &&
                      setVehicules?.map((vehicule: Vehicule) => (
                        <FormGroup key={uniqueId()} className="vehicule_div">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={IsVehiculeChecked(
                                  vehicule.registrationNumber,
                                  details[DETAIL_JUGEMENT_ADD.SUED_PARTIES],
                                )}
                                color="primary"
                                onChange={handleVehiculeChangeCheckBox(
                                  vehicule,
                                )}
                              />
                            }
                            label={
                              featureService.hasFeature(
                                FEATURES.MIGRATION_CLAIM_INFO,
                              ) &&
                              !isEmpty(
                                getVehicleFromSagisInfo(
                                  get(vehicule, 'partKey'),
                                  props.sagisOpposingParties,
                                ),
                              )
                                ? get(
                                    getVehicleFromSagisInfo(
                                      get(vehicule, 'partKey'),
                                      props.sagisOpposingParties,
                                    ),
                                    'lastName',
                                  ) +
                                  ' ' +
                                  get(
                                    getVehicleFromSagisInfo(
                                      get(vehicule, 'partKey'),
                                      props.sagisOpposingParties,
                                    ),
                                    'firstName',
                                  )
                                : vehicule.lastName + ' ' + vehicule.firstName
                            }
                          />
                        </FormGroup>
                      ))}
                  </div>
                </SuedPartiesContainer>
              </>
            )}
          </article>
        </article>
      )}
    </section>
  );
};

ProcedureInformationForm.defaultProps = {
  shouldDisplay: true,
};

export default injectIntl(ProcedureInformationForm);
